import React, { useState, useEffect } from 'react';
import {
     Users, Loader, Calendar, Play, Image, Heart, Star,
     Award, Coffee, Music, Camera, Video, BookOpen,
     Sparkles, Globe, Target, Lightbulb,
    ChevronLeft, ChevronRight, X, PlayCircle
} from 'lucide-react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

const OurCulture = () => {
    // API base URL - keeping consistent with your existing code
    const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

    // State management
    const [cultureData, setCultureData] = useState({
        dailyPrayers: [],
        testimonials: []
    });
    
    const [loading, setLoading] = useState(true);
    const [showVideo, setShowVideo] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState('');

    // YouTube URL conversion function - supports all formats
    const convertToEmbedUrl = (url) => {
        if (!url) return '';
        
        // If already an embed URL, return as is
        if (url.includes('/embed/')) {
            return url;
        }
        
        // Handle different YouTube URL formats
        const patterns = [
            // Standard watch URLs
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
            // Shortened URLs
            /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
            // Mobile URLs
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
            // Embed URLs (just in case)
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
            // Live URLs
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
            // Playlist URLs (extract first video)
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
            // Channel URLs with specific video
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/c\/.*\/([a-zA-Z0-9_-]{11})/,
            // User URLs
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/user\/.*\/([a-zA-Z0-9_-]{11})/
        ];
        
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                const videoId = match[1];
                return `https://www.youtube.com/embed/${videoId}?modestbranding=1&showinfo=0&rel=0&controls=1`;
            }
        }
        
        // If no pattern matches, try to extract video ID from any YouTube URL
        const fallbackPattern = /[?&]v=([a-zA-Z0-9_-]{11})/;
        const fallbackMatch = url.match(fallbackPattern);
        if (fallbackMatch && fallbackMatch[1]) {
            const videoId = fallbackMatch[1];
            return `https://www.youtube.com/embed/${videoId}?modestbranding=1&showinfo=0&rel=0&controls=1`;
        }
        
        // If still no match, return original URL (might be a valid embed URL)
        console.warn('Could not convert YouTube URL:', url);
        return url;
    };

    // Process culture data to ensure all video URLs are in embed format
    const processCultureData = (data) => {
        return {
            ...data,
            dailyPrayers: data.dailyPrayers?.map(prayer => ({
                ...prayer,
                videoUrl: convertToEmbedUrl(prayer.videoUrl)
            })) || []
        };
    };

    // Fetch culture data on component mount
    useEffect(() => {
        fetchCultureData();
    }, []);

    // Fetch culture data from API with fallback
    const fetchCultureData = async () => {
        try {
            setLoading(true);
            
            // Try to fetch from API first
            try {
                const response = await fetch(`${API_BASE_URL}/culture`);
                if (response.ok) {
                    const data = await response.json();
                    const processedData = processCultureData(data.data || data);
                    setCultureData(processedData);
                    return;
                }
            } catch (apiError) {
                console.log('API not available, using fallback data');
            }

            // Fallback demo data with various YouTube URL formats (all will be converted)
            const fallbackData = {
                dailyPrayers: [
                    {
                        id: 1,
                        title: 'Morning Prayer & Meditation',
                        description: 'We start each day with collective prayer and meditation to align our minds and hearts.',
                        time: '9:00 AM',
                        frequency: 'Daily',
                        // Using watch format - will be auto-converted
                        videoUrl: 'https://www.youtube.com/watch?v=80usapbuWPY',
                        thumbnailUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
                        participants: '50+ employees'
                    },
                    {
                        id: 2,
                        title: 'Evening Gratitude Circle',
                        description: 'End the day with gratitude and reflection, sharing positive moments and achievements.',
                        time: '6:30 PM',
                        frequency: 'Daily',
                        // Using shortened format - will be auto-converted
                        videoUrl: 'https://www.youtube.com/watch?v=ZO3yt9oqiVE',
                        thumbnailUrl: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=800&h=600&fit=crop',
                        participants: '30+ employees'
                    },
                    {
                        id: 3,
                        title: 'Midday Mindfulness Session',
                        description: 'A brief pause in the day for mindfulness practice to refresh and refocus.',
                        time: '1:00 PM',
                        frequency: 'Daily',
                        // Already in embed format - will remain unchanged
                        videoUrl: 'https://www.youtube.com/watch?v=Y-2vXBZsB2s',
                        thumbnailUrl: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2?w=800&h=600&fit=crop',
                        participants: '25+ employees'
                    },
                    {
                        id: 4,
                        title: 'Midday Mindfulness Session',
                        description: 'A brief pause in the day for mindfulness practice to refresh and refocus.',
                        time: '1:00 PM',
                        frequency: 'Daily',
                        videoUrl: 'https://www.youtube.com/watch?v=zRYEGtZQkYw',
                        thumbnailUrl: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2?w=800&h=600&fit=crop',
                        participants: '25+ employees'
                    }

                ]
            };
            
            const processedFallbackData = processCultureData(fallbackData);
            setCultureData(processedFallbackData);
            
        } catch (error) {
            console.error('Error fetching culture data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle video play with URL conversion
    const handlePlayVideo = (videoUrl) => {
        const embedUrl = convertToEmbedUrl(videoUrl);
        setCurrentVideoUrl(embedUrl);
        setShowVideo(true);
    };

    // Validate YouTube URL function (optional - for form validation)
    const isValidYouTubeUrl = (url) => {
        if (!url) return false;
        
        const youtubePatterns = [
            /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/,
            /^(https?:\/\/)?m\.youtube\.com\/.+/
        ];
        
        return youtubePatterns.some(pattern => pattern.test(url));
    };

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
        },
        
        // Smaller header styling
        heroSection: {
            background: '#1a2332',
            backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px, 30px 30px',
            padding: '2.5rem 0',
            marginBottom: '0',
            position: 'relative',
            overflow: 'hidden',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
        },
        heroContainer: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem',
            position: 'relative',
            zIndex: 2,
            textAlign: 'center'
        },
        heroTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#ffffff',
            margin: '0 0 0.75rem 0',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
        },
        heroSubtitle: {
            fontSize: '1.125rem',
            color: 'rgba(255,255,255,0.9)',
            margin: '0',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.5'
        },
        
        // Main content
        mainSection: {
            backgroundColor: '#f8fafc',
            padding: '3rem 0',
            flex: 1
        },
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem'
        },
        
        // Section styling
        section: {
            marginBottom: '4rem'
        },
        sectionTitle: {
            fontSize: '2.25rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '1rem',
            textAlign: 'center'
        },
        sectionSubtitle: {
            fontSize: '1.125rem',
            color: '#4a5568',
            marginBottom: '2.5rem',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto 2.5rem auto',
            lineHeight: '1.6'
        },
        
        // Daily prayers section
        prayersGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem',
            marginTop: '2rem'
        },
        prayerCard: {
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
            position: 'relative'
        },
        
        // Loading state
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: '#6b7280'
        },
        
        // Clean Video modal
        videoModal: {
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
        },
        videoContainer: {
            position: 'relative',
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        videoIframe: {
            width: '90vw',
            height: '90vh',
            border: 'none',
            maxWidth: '1200px',
            maxHeight: '675px'
        },
        videoCloseButton: {
            position: 'absolute',
            top: '20px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            border: 'none',
            color: 'white',
            fontSize: '1.5rem',
            cursor: 'pointer',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
        },
        
        // Error message styling
        errorMessage: {
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.875rem'
        }
    };

    if (loading) {
        return (
            <div style={styles.pageContainer}>
                <section style={styles.heroSection}>
                    <div style={styles.heroContainer}>
                        <h1 style={styles.heroTitle}>Our Culture</h1>
                        <p style={styles.heroSubtitle}>
                            Where Values Meet Action
                        </p>
                    </div>
                </section>
                
                <main style={styles.mainSection}>
                    <div style={styles.loadingContainer}>
                        <Loader size={32} className="animate-spin" style={{ marginRight: '0.5rem' }} />
                        <span>Loading culture content...</span>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .play-button:hover {
                    background-color: rgba(0, 0, 0, 0.9);
                    transform: translate(-50%, -50%) scale(1.1);
                }
                
                .video-close-button:hover {
                    background-color: rgba(0, 0, 0, 1);
                }
                
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 1.75rem !important;
                    }
                    
                    .hero-subtitle {
                        font-size: 1rem !important;
                    }
                    
                    .section-title {
                        font-size: 1.75rem !important;
                    }
                    
                    .prayers-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .testimonials-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .container {
                        padding: 0 1rem !important;
                    }
                    
                    .video-iframe {
                        width: 95vw !important;
                        height: 95vh !important;
                    }
                    
                    .hero-section {
                        padding: 2rem 0 !important;
                    }
                    
                    .main-section {
                        padding: 2rem 0 !important;
                    }
                }
            `}</style>
            <Header />

            <div style={styles.pageContainer}>
                {/* Hero Section */}
                <section style={styles.heroSection} className="hero-section">
                    <div style={styles.heroContainer}>
                        <h1 style={styles.heroTitle} className="hero-title">Our Culture</h1>
                        <p style={styles.heroSubtitle} className="hero-subtitle">
                            Where Values Meet Action - Building a Community of Growth, Faith, and Excellence
                        </p>
                    </div>
                </section>

                {/* Main Content */}
                <main style={styles.mainSection} className="main-section">
                    <div style={styles.container} className="container">
                        
                        {/* Daily Prayers Section */}
                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle} className="section-title">Daily Prayers & Meditation</h2>
                            <p style={styles.sectionSubtitle}>
                                We begin and end each day with prayer, creating a peaceful and focused work environment
                            </p>
                            
                            <div style={styles.prayersGrid} className="prayers-grid">
                                {cultureData.dailyPrayers.map((prayer) => {
                                    const embedUrl = convertToEmbedUrl(prayer.videoUrl);
                                    
                                    // Show error if URL couldn't be converted
                                    if (!embedUrl || embedUrl === prayer.videoUrl && !prayer.videoUrl.includes('/embed/')) {
                                        return (
                                            <div key={prayer.id} style={styles.prayerCard}>
                                                <div style={styles.errorMessage}>
                                                    ⚠️ Invalid YouTube URL format. Please check the video URL.
                                                </div>
                                                <div style={{ padding: '1.5rem' }}>
                                                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                                                        {prayer.title}
                                                    </h3>
                                                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                                        Original URL: {prayer.videoUrl}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    }
                                    
                                    return (
                                        <div key={prayer.id} style={{
                                            ...styles.prayerCard,
                                            padding: 0,
                                            height: '300px'
                                        }}>
                                            <iframe
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    border: 'none',
                                                    borderRadius: '16px'
                                                }}
                                                src={embedUrl}
                                                title={prayer.title || "Prayer Video"}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                loading="lazy"
                                            ></iframe>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Culture Stats Section */}
                        <section style={styles.section}>
                            <div style={{
                                backgroundColor: '#1a2332',
                                borderRadius: '16px',
                                padding: '2.5rem 2rem',
                                textAlign: 'center',
                                color: 'white'
                            }}>
                                <h2 style={{
                                    fontSize: '1.875rem',
                                    fontWeight: '700',
                                    marginBottom: '1.5rem',
                                    color: 'white'
                                }}>
                                    Our Culture in Numbers
                                </h2>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '2rem',
                                    maxWidth: '800px',
                                    margin: '0 auto'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: '2.5rem',
                                            fontWeight: '700',
                                            color: '#22c55e',
                                            marginBottom: '0.5rem'
                                        }}>
                                            365
                                        </div>
                                        <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                                            Days of Prayer & Meditation
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{
                                            fontSize: '2.5rem',
                                            fontWeight: '700',
                                            color: '#22c55e',
                                            marginBottom: '0.5rem'
                                        }}>
                                            100%
                                        </div>
                                        <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                                            Values-Driven Approach
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </main>

                {/* Clean Video Modal */}
                {showVideo && (
                    <div style={styles.videoModal}>
                        <div style={styles.videoContainer}>
                            <button 
                                style={styles.videoCloseButton}
                                className="video-close-button"
                                onClick={() => setShowVideo(false)}
                            >
                                <X size={24} />
                            </button>
                            <iframe
                                style={styles.videoIframe}
                                className="video-iframe"
                                src={currentVideoUrl}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                )}

                <Footer />
            </div>
        </>
    );
};

export default OurCulture;